<template>
  <div data-test="outside-vif">
    <b-form-row v-if="supportedType" :class="'form-item ' + wrapperClass">
      <div class="form-group">
        <label
          v-if="fieldLabel.trim().length && getInputType !== 'markup'"
          :for="fieldId"
          :class="labelClass"
          :title="requiredTitle"
        >
          {{ fieldLabel.trim() + getLabelSuffix(fieldLabel) }}
          <span class="sr-only" v-if="isRequired">
            (This field is required)</span
          >
        </label>
        <div
          class="raftjs-hovertip-icon"
          v-if="hasDescription && showLabel && showToolTip"
        >
          &nbsp;
          <b-img
            :id="hovertipId"
            :src="helpIconSrc"
            alt="help"
            style="height: 0.75rem"
          />
          <b-popover :target="hovertipId" triggers="hover focus">
            {{ fieldData?.description }}
          </b-popover>
        </div>
      </div>
      <template v-if="descriptionPosition === 'top' && fieldData?.description">
        <!-- V-HTML used on admin set descriptions for specific formatting -->
        <div
          :key="fieldData?.field_name"
          v-html="fieldData?.description"
          class="form-description"
        ></div>
      </template>

      <div class="form-group-input">
        <div class="validation-border" :class="invalidClass">
          <v-checkbox-group
            v-if="
              (getInputType === 'select' ||
                getInputType === 'checkbox_group') &&
              isMultiSelect &&
              styled
            "
            :options="getOptions"
            :key="fieldId"
            :columns="fieldName === 'field_inc_invest_root_causes' ? 3 : 1"
            :class="fieldClass"
            :required="isRequired"
            :id="fieldId"
            :name="fieldName"
            v-model="multiFieldValue"
            @change="updateValue($event)"
            :selected="multiFieldValue || []"
            :disabled="disabled"
          />
          <v-radio-toggle
            v-else-if="
              ['radio_group', 'radio_group_toggle'].includes(getInputType) &&
              styled
            "
            :options="getOptions"
            :key="fieldName"
            :class="fieldClass"
            :ref="fieldName"
            :required="isRequired"
            :id="fieldName"
            :name="fieldName"
            @change="updateValue($event)"
            v-model="fieldValue"
            :selected="fieldValue"
            size="lg"
            :disabled="disabled"
          />
          <b-form-checkbox-group
            v-else-if="getInputType === 'checkbox_group' && !styled"
            :required="isRequired"
            :disabled="disabled"
            :class="fieldClass"
            :ref="fieldName"
          >
            <b-form-checkbox
              v-for="(option, optionIndex) in getOptions"
              :key="option.value"
              :value="option.value"
              :name="fieldName"
              inline
              v-model="fieldValue"
              @change="updateValue($event)"
            >
              {{ option.text }}
              <template
                v-if="
                  option.hasOwnProperty('description') &&
                  option.description.length
                "
              >
                <b-img
                  :id="fieldName + '-' + optionIndex + '-help'"
                  :src="helpIconSrc"
                  alt="help"
                  class="raft-help"
                  style="height: 0.75rem"
                />
                <b-popover
                  :target="fieldName + '-' + optionIndex + '-help'"
                  triggers="hover focus"
                >
                  <template #title>
                    {{ option.text }}
                  </template>
                  <p>{{ option.description }}</p>
                </b-popover>
              </template>
            </b-form-checkbox>
          </b-form-checkbox-group>
          <radio-set
            v-else-if="getInputType === 'radio'"
            :options="getOptions"
            :class="fieldClass"
            :ref="fieldName"
            :required="isRequired"
            :name="fieldName"
            :id="fieldName"
            @radio-selection="updateValue($event)"
            v-model="fieldValue"
            :disabled="disabled"
          />
          <b-form-radio-group
            v-else-if="getInputType === 'radio_group'"
            :options="getOptions"
            :class="fieldClass"
            :ref="fieldName"
            :key="fieldName"
            :required="isRequired"
            :name="fieldName"
            @change="updateValue($event)"
            v-model="fieldValue"
            size="lg"
            :disabled="disabled"
          />
          <b-form-radio-group
            v-else-if="getInputType === 'radio_group_toggle'"
            :options="getOptions"
            :class="fieldClass"
            :ref="fieldName"
            :key="fieldName"
            :required="isRequired"
            :name="fieldName"
            @change="updateValue($event)"
            v-model="fieldValue"
            buttons
            button-variant="outline-primary"
            size="lg"
            :disabled="disabled"
          />
          <v-radio-toggle
            v-else-if="getInputType === 'inline_button_toggle'"
            :options="getOptions"
            :class="fieldClass"
            :ref="fieldName"
            :key="fieldName"
            :required="isRequired"
            :name="fieldName"
            :data-test="fieldName"
            @change="updateValue($event)"
            v-model="fieldValue"
            selected=""
            :disabled="disabled"
          />
          <b-form-select
            v-else-if="getHtmlElement === 'select' && isMultiSelect && !styled"
            :id="fieldId"
            :class="fieldClass"
            @change="updateValue(multiFieldValue)"
            v-model="multiFieldValue"
            :required="isRequired"
            :name="fieldName"
            :data-test="fieldName"
            :options="getOptions"
            multiple
            :disabled="disabled"
          />
          <b-form-select
            v-else-if="getHtmlElement === 'select' && !styled"
            :id="fieldId"
            :class="fieldClass"
            @change="updateValue(fieldValue)"
            v-model="fieldValue"
            :required="isRequired"
            :name="fieldName"
            :data-test="fieldName"
            :options="getOptions"
            :disabled="disabled || getOptions.length === 0"
          >
            <template #first>
              <b-form-select-option :value="null" :disabled="isRequired">
                -- Select --
              </b-form-select-option>
            </template>
          </b-form-select>
          <wysiwyg-editor-field
            v-else-if="wysiwygCondition"
            field-class="wysiwyg-field"
            :value="getEditorValue(fieldName)"
            :id="fieldId"
            :class="fieldClass"
            ref="wysiwyg"
            :title="null"
            :field-name="fieldName"
            :required="isRequired"
            @update-value="updateValue"
            :disabled="disabled"
            :placeholders="
              fieldData?.hasOwnProperty('placeholders')
                ? fieldData.placeholders
                : []
            "
          />
          <ChemicalSearch
            v-else-if="getInputType === 'chemical_constituent_reference'"
            :field-config="fieldData"
            :disabled="disabled"
          />
          <div class="markup-wrapper" v-else-if="getInputType === 'markup'">
            <!-- V-HTML used on admin set markup for specific formatting -->
            <div
              :class="labelClass"
              v-if="markUpHtml"
              v-html="fieldData?.settings?.markup.value"
            ></div>
            <p v-else>
              {{ fieldData?.settings?.markup.value }}
            </p>
          </div>
          <input
            v-else-if="getHtmlElement === 'input' && getInputType === 'number'"
            :placeholder="getPlaceholder"
            :type="getInputType"
            :id="fieldId"
            :class="fieldClass"
            :ref="fieldName"
            :pattern="getInputPattern"
            :title="null"
            :name="fieldData.field_name"
            :data-test="fieldData?.field_name"
            :accept="getInputAccept"
            :maxlength="getInputMaxLength"
            @input="updateValue(fieldValue)"
            v-model="fieldValue"
            :required="isRequired"
            :max="fieldMax"
            :min="fieldMin"
            :step="fieldStep"
            :disabled="disabled"
          />
          <input
            v-else-if="getHtmlElement === 'input'"
            :placeholder="getPlaceholder"
            :type="getInputType"
            :id="fieldId"
            :class="fieldClass"
            :ref="fieldName"
            :pattern="getInputPattern"
            :title="null"
            :name="fieldData.field_name"
            :data-test="fieldData?.field_name"
            :accept="getInputAccept"
            :maxlength="getInputMaxLength"
            @input="updateValue(fieldValue)"
            v-model="fieldValue"
            :required="isRequired"
            :max="fieldMax"
            :min="fieldMin"
            :step="fieldStep"
            :disabled="disabled"
          />
          <b-form-textarea
            v-else-if="getHtmlElement === 'textarea'"
            :placeholder="getPlaceholder"
            :rows="fieldData.form_display.settings.rows"
            cols="50"
            :id="fieldId"
            :class="fieldClass"
            :ref="fieldName"
            :title="null"
            :name="fieldName"
            :maxlength="getInputMaxLength"
            @input="updateValue(fieldValue)"
            v-model="fieldValue"
            :required="isRequired"
            no-auto-shrink
            :disabled="disabled"
            :autofocus="isAutofocused(fieldName)"
          />
          <datepicker
            v-else-if="getHtmlElement === 'datetime'"
            typeable
            format="yyyy-MM-dd"
            :id="fieldId"
            :class="fieldClass"
            :ref="fieldName"
            :name="fieldName"
            :required="isRequired"
            :highlighted="RaftGenericHelper.getDatepickerHighlighted()"
            :placeholder="RaftGenericHelper.getDatepickerPlaceholder()"
            v-model="fieldValue"
            @input="updateValue(fieldValue)"
            :disabled-dates="disabledDates"
            :disabled="disabled"
          />
          <input
            v-else-if="getHtmlElement === 'time'"
            type="time"
            :class="fieldClass + ' form-time form-text'"
            v-model="fieldValue"
            :id="fieldId"
            :ref="fieldName"
            :name="fieldName"
            :required="isRequired"
            :disabled="disabled"
            @input="updateValue(fieldValue)"
          />
          <og-reference-select
            v-else-if="
              getHtmlElement === 'og_standard_reference' &&
              getEntityReferenceOpts.length
            "
            :id="fieldId"
            :field-class="fieldClass"
            :ref="fieldName"
            :name="fieldName"
            :required="isRequired"
            :value="fieldValue"
            @change="updateValue"
            v-model="fieldValue"
            :options="getEntityReferenceOpts"
            :disabled="disabled"
          />
          <raft-autocomplete
            v-else-if="getInputType === 'autocomplete'"
            field-class="entity-reference-field"
            :ref="fieldName"
            :id="fieldId"
            :default-value="fieldValue"
            :title="null"
            :element-id="fieldId"
            :placeholder="getPlaceholder"
            :source="
              Object.hasOwn(fieldData, 'autoCompleteSource')
                ? fieldData.autoCompleteSource
                : userLookup
            "
            :show-label="
              Object.hasOwn(fieldData, 'showLabel') ? fieldData.showLabel : true
            "
            :results-display="autocompleteFormatter"
            input-class="form-control"
            @validation-error="emitValidationError"
            @selected="updateAutocompleteValue"
            @clear="updateValue"
            :disabled="disabled"
          />
          <ExplainField
            v-else-if="getInputType === 'explain_field'"
            field-class="explain-field"
            :ref="fieldName"
            :field-data="fieldData"
            :field-value="explainFieldDefault"
            :field-id="fieldId"
            :is-required="isRequired"
            @explain-field-update="updateValue"
            :disabled="disabled"
          />
          <DateOffsetField
            v-else-if="getInputType === 'date_offset_field'"
            :field-class="fieldClass"
            :ref="fieldName"
            :field-data="fieldData"
            :field-value="fieldValue"
            :field-id="fieldId"
            :field-name="fieldName"
            :disabled="disabled"
            @change="updateValue"
            :parent-value="parentValue"
            :bundle="bundle"
            :entity-type="entityType"
            :always-frequency="alwaysFrequency"
            :data-test="fieldId"
          />
          <DateRecurrenceField
            v-else-if="getInputType === 'date_recurrence'"
            :field-class="fieldClass"
            :ref="fieldName"
            :field-data="fieldData"
            :field-value="fieldValue"
            :field-id="fieldId"
            :field-name="fieldName"
            :always-frequency="alwaysFrequency"
            :disabled="disabled"
            @change="updateValue"
            :parent-value="parentValue"
            :bundle="bundle"
            :entity-type="entityType"
            :data-test="fieldId"
          />
          <InvalidIcon v-if="invalid" />
        </div>
      </div>
      <template v-if="descriptionPosition === 'bottom'">
        <div class="description" v-if="getHelpText || getFileText">
          <div v-if="uploading" class="text-center text-danger my-2">
            <img
              src="/sites/all/modules/hide_submit/images/squares.gif"
              alt="loading"
            />
            <strong>Uploading...</strong>
          </div>
          <div v-if="getFileText">
            Existing file: <strong>{{ getFileText }}</strong
            ><br />
          </div>
          {{ getHelpText }}
        </div>
      </template>
      <slot name="extraData" :data="this" />
    </b-form-row>
  </div>
</template>

<script>
import datepicker from "vuejs-datepicker";
import EntityFormHelper from "../lib/EntityFormHelper";
import helpIcon from "./raft/help-Outline.svg";
import OgReferenceSelect from "./fields/OgReferenceSelect.vue";
import RadioSet from "./fields/RadioSet.vue";
import RaftAutocomplete from "./raft/RaftAutocomplete.vue";
import RaftGenericHelper from "../lib/raft_generic_helper";
import RaftRestClient from "../lib/raft_rest_client";
import WysiwygEditorField from "./raft/WysiwygEditorField.vue";
import ExplainField from "./raft/ExplainField.vue";
import VCheckboxGroup from "./raft/VCheckboxGroup.vue";
import VRadioToggle from "./raft/VRadioToggle.vue";
import ChemicalSearch from "./raft/ChemicalSearch.vue";
import DateOffsetField from "./raft/DateOffsetField.vue";
import DateRecurrenceField from "./raft/DateRecurrenceField.vue";
import InvalidIcon from "./InvalidIcon.vue";

export default {
  name: "EntityFormField",
  data() {
    return {
      RaftGenericHelper: RaftGenericHelper,
      uploading: false,
      fieldValue: null,
      multiFieldValue: [],
      fileText: "",
      referenceOpts: null,
      typeMap: {},
      helpIconSrc: helpIcon,
    };
  },
  components: {
    datepicker,
    OgReferenceSelect,
    RadioSet,
    RaftAutocomplete,
    WysiwygEditorField,
    ExplainField,
    VCheckboxGroup,
    VRadioToggle,
    ChemicalSearch,
    DateOffsetField,
    DateRecurrenceField,
    InvalidIcon,
  },
  computed: {
    fieldDescription: function () {
      return this.fieldData?.description;
    },
    markUpHtml: function () {
      return this.fieldData?.settings?.markup?.format === "basic_html";
    },
    requiredTitle: function () {
      return this.isRequired ? "This field is required." : null;
    },
    disabledDates: function () {
      const fromDate = new Date();
      fromDate.setMinutes(59);
      fromDate.setHours(23);

      if (
        this.fieldData.form_display.third_party_settings.hasOwnProperty(
          "bioraft_core_components",
        )
      ) {
        if (
          this.fieldData.form_display.third_party_settings.bioraft_core_components.hasOwnProperty(
            "datepicker",
          )
        ) {
          if (
            this.fieldData.form_display.third_party_settings
              .bioraft_core_components.datepicker.disable_future_dates
          ) {
            return {
              from: fromDate,
            };
          }
        }
      }
      return {};
    },
    fieldLabel: function () {
      if (!this.showLabel) {
        return "";
      }
      if (
        this.fieldData?.form_display?.third_party_settings?.hasOwnProperty(
          "bioraft_core_components",
        )
      ) {
        if (
          this.fieldData?.form_display?.third_party_settings?.bioraft_core_components?.hasOwnProperty(
            "label_override",
          )
        ) {
          return this.fieldData?.form_display?.third_party_settings
            ?.bioraft_core_components?.label_override;
        }
      }
      return this.fieldData.label;
    },
    fieldName: function () {
      return this.fieldData.field_name;
    },
    fieldId: function () {
      return this.fieldName + "_vfield";
    },
    fieldDefaultValue: function () {
      return this.fieldData.default_value;
    },
    labelClass: function () {
      const value = ["vlabel"];
      if (this.isRequired) {
        value.push("form-required");
      }
      if (this.hasDescription) {
        value.push("raftjs-hovertip");
      }
      return value.join(" ");
    },
    isMultiSelect: function () {
      return (
        this.getHtmlElement === "select" &&
        this.fieldData?.field_storage?.cardinality === -1
      );
    },
    fieldClass: function () {
      return "vfield form-control " + this.addClass;
    },
    hasDescription: function () {
      if (
        this.fieldData.hasOwnProperty("description") &&
        this.fieldData.description !== null
      ) {
        return this.fieldData.description.length > 0;
      }
      return false;
    },
    getHelpText: function () {
      return EntityFormHelper.getHelpText(this.fieldData);
    },
    fieldMax: function () {
      if (this.getInputType === "number") {
        if (typeof this.fieldData.settings.max === "number") {
          return this.fieldData.settings.max;
        }
      }
      return null;
    },
    fieldMin: function () {
      if (this.getInputType === "number") {
        if (typeof this.fieldData.settings.min === "number") {
          return this.fieldData.settings.min;
        }
      }
      return null;
    },
    fieldStep: function () {
      if (this.getInputType === "number") {
        const scale = this.fieldData.settings.scale;
        if (typeof scale === "number") {
          // Use toFixed() since computer math doesn't give a precise answer.
          return (0.1 ** scale).toFixed(scale);
        }
      }
      return null;
    },
    hovertipId: function () {
      return this.fieldName + "-vht";
    },
    getHtmlElement: function () {
      return EntityFormHelper.getHtmlElement(
        this.fieldData?.field_type,
        this.fieldData,
      );
    },
    getInputType: function () {
      return EntityFormHelper.getInputType(
        this.fieldData?.field_type,
        this.fieldData,
      );
    },
    supportedType: function () {
      const el = this.getHtmlElement;
      return el !== "input" || this.getInputType;
    },
    getOptions: function () {
      return EntityFormHelper.getOptions(this.fieldData);
    },
    getEntityReferenceOpts: function () {
      if (this.referenceOpts) {
        return this.referenceOpts;
      }
      if (
        typeof this.fieldData.form_display.third_party_settings
          .bioraft_core_components === "undefined"
      ) {
        return [];
      }
      if (
        typeof this.fieldData.form_display.third_party_settings
          .bioraft_core_components.rest_path !== "undefined"
      ) {
        const path =
          this.fieldData.form_display.third_party_settings
            .bioraft_core_components.rest_path;
        const dataKey =
          this.fieldData.form_display.third_party_settings
            .bioraft_core_components.rest_key;
        RaftRestClient.getRest(
          path,
          {},
          (data) => {
            this.referenceOpts = this.getOptsFromRestResult(data[dataKey]);
          },
          this.errorEmit,
          this,
        );
      }
      return [];
    },
    getInputPattern: function () {
      return EntityFormHelper.getInputPattern(this.fieldData);
    },
    getInputAccept: function () {
      return EntityFormHelper.getInputAccept(this.fieldData);
    },
    getInputMaxLength: function () {
      return EntityFormHelper.getInputMaxLength(this.fieldData);
    },
    getFileText: function () {
      if (
        this.fieldData?.field_type !== "file" &&
        this.fieldData?.field_type !== "image"
      ) {
        return false;
      }
      return this.fileText;
    },
    isRequired: function () {
      if (
        (this.fieldData?.field_type === "file" ||
          this.fieldData?.field_type === "image") &&
        this.fileText
      ) {
        return false;
      }
      return this.fieldData.required;
    },
    getPlaceholder: function () {
      if (this.placeholder) {
        return this.placeholder;
      }
      return null;
    },
    wysiwygCondition: function () {
      if (this.getHtmlElement !== "input" || this.getInputType !== "wysiwyg") {
        return false;
      }
      return true;
    },
    explainFieldDefault: function () {
      if (this.fieldValue !== "") {
        return this.fieldValue;
      }
      return {};
    },
    invalidClass() {
      return this.invalid ? " invalid" : "";
    },
  },
  mounted() {
    if (
      this.fieldData?.field_type !== "file" &&
      this.fieldData?.field_type !== "image"
    ) {
      if (this.isMultiSelect) {
        this.multiFieldValue = this.parentValue || [];
      }
      this.fieldValue = this.parentValue;
    } else if (this.entity) {
      const path =
        this.entityType +
        "/" +
        this.bundle +
        "/" +
        this.entity.id +
        "/" +
        this.fieldData.field_name;
      EntityFormHelper.getJSONapi(
        path,
        "",
        this.updateFile,
        this.errorEmit,
        this,
      );
    }
    // Set today as the default date.
    if (
      this.fieldData?.field_type === "datetime" &&
      !this.fieldValue &&
      !this.noDefaultValue
    ) {
      this.fieldValue = new Date();
      this.updateValue(this.fieldValue);
    }
    // Handle default selected values.
    if (this.fieldData?.field_type === "boolean" && !this.noDefaultValue) {
      if (
        this.entity &&
        this.entity.hasOwnProperty(this.fieldData.field_name)
      ) {
        this.fieldValue = !!this.entity[this.fieldData.field_name];
      } else if (
        this.fieldDefaultValue[0] &&
        this.fieldDefaultValue[0].hasOwnProperty("value")
      ) {
        this.fieldValue = !!this.fieldDefaultValue[0].value;
      }
      this.updateValue(this.fieldValue);
    }

    if (
      this.fieldData?.field_type === "list_string" &&
      !this.fieldValue &&
      !this.noDefaultValue
    ) {
      if (
        this.fieldDefaultValue?.[0] &&
        this.fieldDefaultValue[0].hasOwnProperty("value")
      ) {
        this.fieldValue = this.fieldDefaultValue[0].value ?? "";
        this.updateValue(this.fieldValue);
      }
    }
  },
  props: {
    fieldData: {
      type: Object,
      required: true,
    },
    parentValue: {
      type: [String, Object, Array, Number, Boolean, Date],
      default: null,
    },
    entityType: {
      type: String,
      required: true,
    },
    bundle: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      default: null,
      required: false,
    },
    placeholder: {
      type: [String, Array],
      default: "",
      required: false,
    },
    noDefaultValue: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    styled: {
      type: Boolean,
      default: false,
    },
    addClass: {
      type: String,
      default: "",
    },
    wrapperClass: {
      type: String,
      required: false,
      default: "",
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    showToolTip: {
      type: Boolean,
      default: true,
    },
    alwaysFrequency: {
      type: Boolean,
      required: false,
      default: false,
    },
    autocompleteFormatter: {
      type: Function,
      default: (value) => {
        // eslint-disable-next-line
        console.warn("Missing autocomplete formatter prop.");
        return value.name ?? value;
      },
    },
    invalid: { type: Boolean, default: false },
    descriptionPosition: {
      type: String,
      default: "bottom",
    },
    suppressLabelColon: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getLabelSuffix(fieldLabel) {
      if (this.suppressLabelColon || fieldLabel.endsWith("?")) {
        return "";
      }
      return ":";
    },
    getEditorValue(fieldId) {
      if (
        Object.keys(this.entity).length > 0 &&
        this.entity.attributes[fieldId]
      ) {
        return this.entity.attributes[fieldId].value;
      }
      return "";
    },
    isAutofocused(fieldName) {
      return (
        [
          "field_inc_invest_action_text",
          "field_incident_invest_activity",
        ].indexOf(fieldName) > -1
      );
    },
    updateWysiwygValue() {
      const value = this.$refs.wysiwyg.getCurrentValue();
      this.updateValue(value);
    },
    updateValue(value) {
      if (this.fieldData.field_type === "file") {
        this.uploading = true;
        this.$emit("update-uploading-status", true);
      }
      if (this.fieldData.field_type === "datetime") {
        if (!RaftGenericHelper.isDateEnabled(value, this.disabledDates)) {
          this.$emit("date-error-message", {
            message: "datetime selected out of range",
            data: value,
          });
          this.errorEmit("datetime selected out of range", value);
          value = new Date();
          this.fieldValue = value;
        }
        value = RaftGenericHelper.datepickerValueToIso(value);
      }
      if (this.fieldData.field_type === "og_standard_reference") {
        value = {
          id: value,
          type: "node--" + this.typeMap[value],
        };
      }
      this.$emit("disable-form-submit");
      EntityFormHelper.updateFormData(
        this.fieldData,
        value,
        this.$refs,
        this.entityType,
        this.bundle,
        this.updateEmit,
        this.errorEmit,
        this,
      );
    },
    updateEmit(value) {
      if (this.fieldData.field_type === "file") {
        this.uploading = false;
        this.$emit("update-uploading-status", false);
      }
      this.$emit("update-form-data", {
        field_name: this.fieldName,
        field_value: value,
      });
    },
    updateFile(fileData) {
      if (fileData.hasOwnProperty("data") && fileData.data) {
        const parts = fileData.data.attributes.uri.url.split("/");
        this.fileText = decodeURIComponent(parts.pop());
      }
    },
    updateAutocompleteValue(value) {
      if (this.setSelectedUser) {
        this.setSelectedUser(value);
        return;
      }
      this.updateValue(value);
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    getOptsFromRestResult: function (data) {
      const opts = [];
      const that = this;
      Object.keys(data).forEach((key) => {
        opts.push({ value: data[key].uuid, text: data[key].title });
        that.typeMap[data[key].uuid] = data[key].type;
      });
      return opts;
    },
    emitValidationError(payload) {
      this.$emit("validation-error", payload);
    },
  },
  watch: {
    parentValue: {
      deep: true,
      handler: function (newValue) {
        if (
          this.fieldData.field_type !== "file" &&
          this.fieldData.field_type !== "image"
        ) {
          if (this.isMultiSelect) {
            this.multiFieldValue = newValue || [];
          }
          this.fieldValue = newValue;
        }
      },
    },
  },
};
</script>

<style scoped>
.validation-border:focus-within {
  outline: var(--blue-900) auto 1px;
  outline: -webkit-focus-ring-color auto 1px;
}

.validation-border :deep(input:focus-visible) {
  outline: none;
}

.validation-border {
  display: grid;
  grid-template-columns: max-content max-content;
  align-items: center;
  height: 100%;
}

.validation-border.invalid {
  border: 2px solid var(--red-900);
  border-radius: 4px;
}

.validation-border.invalid:focus-within {
  border-color: transparent;
}

.validation-border.invalid :deep(input) {
  border-color: transparent;
}

.validation-border :deep(input) {
  padding: 0.1875rem;
}

.form-group-input {
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.2rem;
}
</style>
